"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ISDESubsidyCalculator = void 0;
const sum_1 = require("../utils/sum");
const utils_1 = require("./utils");
class ISDESubsidyCalculator {
    /**
     * Get the details about subsidy for this year for this solution, i.e. minimum and maximum amount of this solution that must be installed.
     */
    getDetailsBySolution(solution) {
        return this.bySolution[solution];
    }
    /**
     * Returns all the solutions for which ISDE subsidy can be requested this year.
     */
    getSubsidizableSolutions() {
        return Object.keys(this.bySolution).filter(solution => this.getDetailsBySolution(solution) !== null);
    }
    /**
     * Calculates ISDE subsidy for a single solution and the amount installed,
     * ignoring any requirements for multiple solutions to be installed and installation date.
     * Returns 0 if subsidy is not possible for this solution or the installed amount.
     *
     * Note: if you want to get a more accurate calculation of the ISDE subsidy for a quote / job, use {@link forInstallable}.
     * If you want to get a more accurate calculation for a set of quotes / jobs, use {@link forInstallables}.
     *
     * @param solution the solution
     * @param amountInstalled the amount of the solution that is being installed.
     * For the insulation solutions, this amount is area in m2, for heat pumps it is the amount of heat pumps installed (usually 1).
     */
    forSingleSolution(solution, amountInstalled) {
        const details = this.getDetailsBySolution(solution);
        if (!details)
            return 0;
        if (amountInstalled < details.amount.min)
            return 0;
        const maxSubsidizedAmount = Math.min(amountInstalled, details.amount.max);
        return maxSubsidizedAmount * details.subsidy();
    }
    /**
     * Calculates ISDE subsidy for one installable, i.e., a quote or a job, augmented with an installation date when known.
     * The installable's items must have product information on it, primarily the ISDE code and optionally an override for
     * the subsidy per unit of that item.
     *
     * - If the installable has an installation date, then this date must be within {@link eligibleWithin}.
     * - The solution of this installable must be subsidisable in this year
     * - The minimum amount of subsidisable product for this solution must be met.
     * - The maximum amount of subsidisable product for this solution will not exceed the maximum that can be subsidised for this solution this year.
     * - This method by default ignores any ISDE requirements on multiple solutions being installed and will always return the maximum subsidy
     *   that can be requested for this quote assuming that it complies with the requirements on multiple solutions.
     */
    forInstallable(installable) {
        if (installable.installationDate) {
            if (installable.installationDate < this.eligibleWithin.start)
                return 0;
            if (this.eligibleWithin.end && installable.installationDate > this.eligibleWithin.end)
                return 0;
        }
        // the solution must be subsidisable in this year
        const details = this.getDetailsBySolution(installable.solution);
        if (!details)
            return 0;
        // the minimum amount of subsidisable product for this solution must be met
        const subsidisableItems = installable.items.filter(item => (0, utils_1.isSubsidizableProduct)(item.product));
        const totalAmount = (0, sum_1.sum)(subsidisableItems.map(item => item.amount));
        if (totalAmount < details.amount.min)
            return 0;
        // calculate and return the total subsidy
        let totalAmountSubsidised = 0;
        return (0, sum_1.sum)(subsidisableItems.map(item => {
            // the amount of products subsidised may not exceed the maximum amount that can be subsidised for this solution
            const amount = Math.min(item.amount, details.amount.max - totalAmountSubsidised);
            const subsidy = amount * details.subsidy(item.product);
            totalAmountSubsidised += amount;
            return subsidy;
        }));
    }
}
exports.ISDESubsidyCalculator = ISDESubsidyCalculator;
